/**
 * The frame every analysis is rendered inside.
 *
 * It owns the parts that must be identical across all five: the AI disclosure,
 * the run state, the two disclosure modals, and the limitations band. Putting
 * them here rather than in each report is what stops the fifth analysis from
 * quietly shipping without a limitations band because nobody remembered.
 *
 * ORDER IS THE CORRECTION. The rejected layout opened with a title, an AI
 * badge, a full-width status card and two disclosure buttons — four blocks of
 * apparatus before a single finding. The conclusion and its visualisation now
 * come first, and every technical disclosure is a compact secondary action
 * underneath. Nothing was removed; the apparatus stopped outranking the answer.
 *
 * A STATUS CARD IS ONLY SHOWN WHEN IT IS ACTIONABLE. "Ready" told the reader
 * nothing they could not see from the report sitting under it, so a fresh,
 * ready run now states its freshness in one provenance line instead. Generating,
 * failed, stale, insufficient and not-generated all still take the full card,
 * because each of those changes what the reader should do next.
 *
 * THE LIMITATIONS BAND IS NOT A DISCLAIMER. A disclaimer says "this might be
 * wrong" about everything and therefore about nothing. This states the specific
 * ways THIS run's search was narrower than intended: a provider that failed, a
 * filter nobody could apply, a budget that ran out. Each one is a fact about
 * this report, recorded by the pipeline, and each one changes how much weight
 * the reader should give a particular gap. It is compact — a labelled action
 * carrying its own count — but it is never absent and never silent.
 */

import React, { useState } from 'react';
import { Text, View } from 'react-native';
import { BookOpen, FileText, Info, TriangleAlert } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { SectionDivider } from '@/components/SectionDivider';
import { AiTransparencyLabel } from '@/components/ai-transparency-label';
import { useAnalysisVisuals } from './analysis-visuals';
import {
  ANALYSIS_LABELS,
  type AnalysisType,
  type ClaimView,
  type EvidenceView,
  type ResearchCoverageView,
} from '@/lib/strategic-analysis/types';
import type { ResolvedRunState } from '@/lib/strategic-analysis/status';
import { AnalysisStatusCard } from './AnalysisStatusCard';
import { RowDivider, SectionCard, SecondaryAction } from './AnalysisPrimitives';
import { FrameworkInfoModal } from './FrameworkInfoModal';
import { InputsUsedModal, type InputsUsedSnapshot } from './InputsUsedModal';
import { MethodologyModal, type DerivationRecord } from './MethodologyModal';
import { SourceEvidenceModal } from './SourceEvidenceModal';
import { APP_LOCALE } from '@/lib/format';

export interface AnalysisShellProps {
  analysisType: AnalysisType;
  /**
   * `null` means a CONTRACT PROBLEM owns this screen — the report exists but
   * cannot be drawn, and a card above already says so. There is no run state to
   * report, and the run vocabulary has no member for one; forcing a value here
   * is what printed "Not generated yet" over a published report.
   */
  state: ResolvedRunState | null;
  failureCode?: string | null;
  isStale?: boolean;
  showingPrevious?: boolean;
  /** Only an active startup member gets generate/refresh controls. */
  canGenerate?: boolean;
  onGenerate?: () => void;
  onRefresh?: () => void;
  coverage: ResearchCoverageView | null;
  derivation: DerivationRecord | null;
  snapshot: InputsUsedSnapshot | null;
  sources: EvidenceView[];
  /**
   * Whether a report body is actually drawn below.
   *
   * The provenance line captions the DISPLAYED report. When nothing is
   * displayed it captions nothing, and read as a statement about the analysis it
   * is simply wrong — which is what it became the moment a refresh failed.
   */
  reportShown?: boolean;
  /**
   * When the newest PUBLISHED analysis landed, whatever has happened since.
   *
   * Survives a failed refresh on purpose. Everything else on this screen hangs
   * off `displayedRunId`, which is null for every state except `ready` and
   * `legacy_report` — so without this the screen cannot say how old the analysis
   * the founder already has is, having forgotten which one it was.
   */
  lastPublishedAt?: string | null;
  onInspectSource?: (fieldKey: string, sourceKey: string) => void;
  /**
   * The report body. Absent while there is nothing publishable to show.
   *
   * Given as a FUNCTION when the body needs to open the source-evidence modal
   * — which the shell owns, because every analysis shows the same one. This
   * was the missing wire: the modal, the claims and the retained sources were
   * all built and none of them were connected, so "View sources" never
   * rendered anywhere and the modal could not be opened at all.
   */
  children?: React.ReactNode | ((inspectClaim: (claim: ClaimView) => void) => React.ReactNode);
}

/**
 * Every recorded way this search was narrower than planned.
 *
 * Derived from the coverage record rather than written by hand, so a limitation
 * the pipeline recorded cannot be omitted by a component that forgot about it.
 */
export function limitationsFor(coverage: ResearchCoverageView | null): string[] {
  if (!coverage) return [];
  const limitations: string[] = [];

  if (coverage.budgetExhausted) {
    limitations.push(
      'The research budget was reached before every element could be searched. Anything unsearched is shown as unknown.',
    );
  }
  const unsearched = coverage.elements.filter(
    (e) => e.state === 'skipped' || e.state === 'budget_exhausted',
  ).length;
  if (unsearched > 0) {
    limitations.push(`${unsearched} element(s) were not searched.`);
  }
  // RETRIEVED IS NOT SUPPORTED, and the band has to say so. An element whose
  // search returned pages nothing has classified used to be counted as covered,
  // so the one place that states how this run fell short went quiet about it.
  const retrieved = coverage.elements.filter((e) => e.state === 'retrieved').length;
  if (retrieved > 0) {
    limitations.push(
      `For ${retrieved} element(s), sources were retrieved but nothing established what they show about the conclusion.`,
    );
  }
  // NAMED EXPLICITLY, because the alternative reads as an absence of evidence.
  // The sources were found and fetched; what stopped is that no approved
  // decision permits sending them to a model. A founder can act on that — it is
  // a product limitation, not a finding about their market.
  const policyLimited = coverage.elements.filter((e) => e.state === 'policy_limited').length;
  if (policyLimited > 0) {
    limitations.push(
      `For ${policyLimited} element(s), sources were retrieved but policy does not permit AI ` +
        `processing of them, so no observations could be extracted. This is a processing ` +
        `limitation, not an absence of evidence.`,
    );
  }
  if (coverage.counterEvidenceCoverage.limited > 0) {
    limitations.push(
      `${coverage.counterEvidenceCoverage.limited} element(s) could not have a complete search for evidence against them.`,
    );
  }
  if (coverage.counterEvidenceCoverage.noneFound > 0) {
    limitations.push(
      `For ${coverage.counterEvidenceCoverage.noneFound} element(s), a search for evidence against the conclusion found none. That is a limit of this search, not proof that none exists.`,
    );
  }
  for (const warning of coverage.providerWarnings) {
    limitations.push(warning.message);
  }
  return limitations;
}

/**
 * Whether the run state is something the reader can act on.
 *
 * Exported so the rule is assertable without rendering — and so "we decided not
 * to show a status card" stays one decision rather than a condition copied into
 * each of the five screens.
 */
export function statusIsActionable(args: {
  state: ResolvedRunState | null;
  isStale: boolean;
  showingPrevious: boolean;
}): boolean {
  // No run state means a problem card is already explaining the screen and
  // carrying its own action. A second card underneath it can only contradict.
  if (args.state === null) return false;
  return args.state !== 'ready' || args.isStale || args.showingPrevious;
}

export function AnalysisShell({
  analysisType,
  state,
  failureCode,
  isStale = false,
  showingPrevious = false,
  canGenerate = false,
  onGenerate,
  onRefresh,
  coverage,
  derivation,
  snapshot,
  sources,
  reportShown = false,
  lastPublishedAt = null,
  onInspectSource,
  children,
}: AnalysisShellProps) {
  const v = useAnalysisVisuals();
  const [infoOpen, setInfoOpen] = useState(false);
  const [inputsOpen, setInputsOpen] = useState(false);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [limitationsOpen, setLimitationsOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<ClaimView | null>(null);

  const limitations = limitationsFor(coverage);
  const showStatus = statusIsActionable({ state, isStale, showingPrevious });

  return (
    <View className="gap-4">
      {/* Provenance in one compact line: what produced this, when, and how much
          evidence stands behind it. The AI disclosure pill is the EU AI Act
          Art. 50 label and stays a tap away from the full disclosure page. */}
      <View className="flex-row items-center gap-2">
        <AiTransparencyLabel variant="badge" context="strategic-analysis" />
        <Text numberOfLines={2} className="flex-1 text-[11px] leading-4" style={{ color: v.colors.text.secondary }}>
          {reportShown
            ? provenanceLine(snapshot, sources.length, coverage)
            : lastAnalysisLine(lastPublishedAt)}
        </Text>
        {/* Icon-only: dimensions live on the Pressable. See the back button. */}
        <AnimatedPressable
          onPress={() => setInfoOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={`About ${ANALYSIS_LABELS[analysisType]}`}
          className="w-9 h-9 rounded-full items-center justify-center shrink-0"
          hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
          style={{ backgroundColor: v.colors.surface.secondary }}
        >
          <Info size={17} color={v.colors.text.secondary} />
        </AnimatedPressable>
      </View>

      {showStatus && state !== null && (
        <AnalysisStatusCard
          state={state}
          failureCode={failureCode}
          canGenerate={canGenerate}
          onGenerate={onGenerate}
          onRefresh={onRefresh}
          isStale={isStale}
          showingPrevious={showingPrevious}
        />
      )}

      {typeof children === 'function' ? children(setSelectedClaim) : children}

      {/* Every technical disclosure, in ONE delimited card after the finding.
          Loose full-width buttons floating under a report read as page
          furniture; a card under a labelled divider reads as a section the
          reader can choose to open. All of them stay reachable from every
          state including a failed one — what was searched is exactly what a
          reader needs when a run produced no answer. */}
      <SectionDivider label="Report details" marginTop={4} marginBottom={4} />
      <SectionCard padded={false}>
        <SecondaryAction
          label="Inputs used"
          icon={<FileText size={15} color={v.colors.text.secondary} />}
          onPress={() => setInputsOpen(true)}
          full
        />
        <RowDivider />
        <SecondaryAction
          label="How this was derived"
          icon={<BookOpen size={15} color={v.colors.text.secondary} />}
          onPress={() => setMethodologyOpen(true)}
          full
        />
        {limitations.length > 0 && (
          <>
            <RowDivider />
            <SecondaryAction
              label="Evidence limitations"
              icon={<TriangleAlert size={15} color={v.colors.amber.main} />}
              count={limitations.length}
              onPress={() => setLimitationsOpen((open) => !open)}
              full
            />
          </>
        )}

        {limitations.length > 0 && limitationsOpen && (
          <View
            accessibilityRole="summary"
            accessibilityLabel="Evidence limitations"
            className="mx-2 mb-2 rounded-xl p-3 gap-2"
            style={{ backgroundColor: v.colors.amber.light, overflow: 'hidden' }}
          >
            {limitations.map((limitation, i) => (
              <Text key={`limitation-${i}`} className="text-[13px] leading-5" style={{ color: v.colors.text.secondary }}>
                {limitation}
              </Text>
            ))}
          </View>
        )}
      </SectionCard>

      <FrameworkInfoModal
        visible={infoOpen}
        onClose={() => setInfoOpen(false)}
        analysisType={analysisType}
      />
      <InputsUsedModal
        visible={inputsOpen}
        onClose={() => setInputsOpen(false)}
        snapshot={snapshot}
        onInspectSource={onInspectSource}
      />
      <MethodologyModal
        visible={methodologyOpen}
        onClose={() => setMethodologyOpen(false)}
        coverage={coverage}
        derivation={derivation}
      />
      <SourceEvidenceModal
        visible={selectedClaim !== null}
        onClose={() => setSelectedClaim(null)}
        claim={selectedClaim}
        sources={sources}
      />
    </View>
  );
}

/**
 * "Updated 13 Aug 2026 · 18 sources".
 *
 * Every part is omitted rather than guessed: a run with no recorded generation
 * time says nothing about when it ran, and a report whose evidence has not
 * loaded does not claim a source count of zero.
 */
/**
 * What to say when no report is drawn.
 *
 * The two questions a founder has at that moment are "what do I already have"
 * and "did the refresh work". The cards below answer the second; this answers
 * the first, and answering neither is what made a completed run look like a dead
 * button. An absence is stated as an absence — never as a missing date.
 */
function lastAnalysisLine(lastPublishedAt: string | null): string {
  if (!lastPublishedAt) return 'No analysis published yet';
  const parsed = new Date(lastPublishedAt);
  if (Number.isNaN(parsed.getTime())) return 'No analysis published yet';
  const on = parsed.toLocaleDateString(APP_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `Last analysis ${on}`;
}

function provenanceLine(
  snapshot: InputsUsedSnapshot | null,
  sourceCount: number,
  coverage: ResearchCoverageView | null,
): string {
  const parts: string[] = ['Web research'];
  const at = snapshot?.generatedAt ?? coverage?.retrievedAt;
  if (at) {
    const parsed = new Date(at);
    if (!Number.isNaN(parsed.getTime())) {
      parts.push(
        `Updated ${parsed.toLocaleDateString(APP_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' })}`,
      );
    }
  }
  if (sourceCount > 0) parts.push(`${sourceCount} source${sourceCount === 1 ? '' : 's'}`);
  return parts.join(' · ');
}
